import React, { forwardRef, useState } from "react";

import { cn } from "../../utils/cn";
import IconEyeClosed from "../icons/IconEyeClosed";
import IconEyeOpen from "../icons/IconEyeOpen";

import { Typography } from "./Typography";


// Input types
export type InputType =
	| "text"
	| "email"
	| "password"
	| "number"
	| "tel"
	| "url"
	| "search"
	| "textarea"
	| "checkbox"
	| "radio"
	| "file";

// Input sizes
export type InputSize = "sm" | "md" | "lg";

// Input states
export type InputState = "default" | "error" | "success" | "disabled";

// Sub-component types
export type SubComponentType = "checkbox" | "radio" | "link" | "text" | "icon";

export interface SubComponentProps {
	type: SubComponentType;
	label?: string;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
	onClick?: () => void;
	href?: string;
	icon?: React.ReactNode;
	className?: string;
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
	// Basic props
	label?: string;
	placeholder?: string;
	helperText?: string;
	errorText?: string;

	// Styling and variants
	inputSize?: InputSize; // Renamed to avoid conflict with HTML size attribute
	state?: InputState;
	variant?: "default" | "outlined" | "filled";

	// Sub-components
	subComponent?: SubComponentProps;

	// Layout
	fullWidth?: boolean;
	required?: boolean;

	// Customization
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	className?: string;
	labelClassName?: string;
	inputClassName?: string;

	// Validation
	showError?: boolean;
	showSuccess?: boolean;

	// File input specific props
	accept?: string;
	multiple?: boolean;
	onFileSelect?: (files: FileList | null) => void;

	// Events
	onValueChange?: (value: string) => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
	// Basic props
	label,
	placeholder,
	helperText,
	errorText,

	// Styling and variants
	inputSize = "md",
	state = "default",
	variant = "default",

	// Sub-components
	subComponent,

	// Layout
	fullWidth = false,
	required = false,

	// Customization
	leftIcon,
	rightIcon,
	className,
	labelClassName,
	inputClassName,

	// Validation
	showError = false,
	showSuccess = false,

	// File input specific props
	accept,
	multiple,
	onFileSelect,

	// Events
	onValueChange,
	onChange,

	// HTML input props
	type = "text",
	disabled,
	...props
}, ref) => {
	const [isFocused, setIsFocused] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	// Determine if we should show error state
	const shouldShowError = showError || state === "error";
	const shouldShowSuccess = showSuccess || state === "success";
	const isDisabled = disabled || state === "disabled";

	// Size mappings
	const getSizeClasses = () => {
		switch (inputSize) {
			case "sm":
				return {
					container: "h-10",
					label: "txt-14" as const,
					input: "text-txt-14 px-3",
					icon: "w-4 h-4"
				};
			case "md":
				return {
					container: "h-12",
					label: "txt-16" as const,
					input: "text-txt-16 px-4",
					icon: "w-5 h-5"
				};
			case "lg":
				return {
					container: "h-14",
					label: "txt-18" as const,
					input: "text-txt-18 px-5",
					icon: "w-6 h-6"
				};
			default:
				return {
					container: "h-12",
					label: "txt-16" as const,
					input: "text-txt-16 px-4",
					icon: "w-5 h-5"
				};
		}
	};

	// State-based styling
	const getStateClasses = () => {
		if (isDisabled) {
			return {
				border: "border-grigio",
				text: "text-grigio",
				bg: "bg-grigio/20"
			};
		}

		if (shouldShowError) {
			return {
				border: "border-rosso",
				text: "text-rosso",
				bg: "bg-bianco"
			};
		}

		if (shouldShowSuccess) {
			return {
				border: "border-verde",
				text: "text-verde",
				bg: "bg-bianco"
			};
		}

		if (isFocused) {
			return {
				border: "border-blu-chiaro",
				text: "text-blu-scuro",
				bg: "bg-bianco"
			};
		}

		return {
			border: "border-blu-scuro",
			text: "text-blu-scuro",
			bg: "bg-bianco"
		};
	};

	// Variant-based styling
	const getVariantClasses = () => {
		switch (variant) {
			case "filled":
				return "bg-grigio/10";
			case "outlined":
				return "bg-transparent";
			default:
				return "bg-bianco";
		}
	};

	const sizes = getSizeClasses();
	const stateClasses = getStateClasses();
	const variantClasses = getVariantClasses();

	// Handle input change
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) {
			onChange(e);
		}
		if (onValueChange) {
			onValueChange(e.target.value);
		}
	};

	// Handle file input change
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) {
			onChange(e);
		}
		if (onFileSelect) {
			onFileSelect(e.target.files);
		}
	};

	// Handle checkbox/radio change
	const handleSubComponentChange = (checked: boolean) => {
		if (subComponent?.onChange) {
			subComponent.onChange(checked);
		}
	};

	// Render sub-component
	const renderSubComponent = () => {
		if (!subComponent) return null;

		switch (subComponent.type) {
			case "checkbox":
				return (
					<div className="flex gap-2 items-center mt-2">
						<div className="relative shrink-0 size-4">
							<input
								type="checkbox"
								checked={subComponent.checked}
								onChange={(e) => handleSubComponentChange(e.target.checked)}
								className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
							/>
							<div className={cn(
								"w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center",
								subComponent.checked
									? "bg-blu-medio border-blu-medio"
									: "bg-bianco border-blu-medio hover:border-blu-chiaro"
							)}>
								{subComponent.checked && (
									<svg
										className="w-2.5 h-2.5 text-bianco"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
											clipRule="evenodd"
										/>
									</svg>
								)}
							</div>
						</div>
						<Typography
							variant="txt-16"
							color="blu-medio"
							onClick={() => handleSubComponentChange(!subComponent.checked)}
							className="font-medium leading-[1.2]"
						>
							{subComponent.label}
						</Typography>
					</div>
				);

			case "radio":
				return (
					<div className="flex gap-2 items-center mt-2">
						<div className="relative shrink-0 size-4">
							<input
								type="radio"
								checked={subComponent.checked}
								onChange={(e) => handleSubComponentChange(e.target.checked)}
								className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
							/>
							<div className={cn(
								"w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center",
								subComponent.checked
									? "bg-blu-medio border-blu-medio"
									: "bg-bianco border-blu-medio hover:border-blu-chiaro"
							)}>
								{subComponent.checked && (
									<div className="w-2 h-2 bg-bianco rounded-full"></div>
								)}
							</div>
						</div>
						<Typography
							variant="txt-16"
							color="blu-medio"
							onClick={() => handleSubComponentChange(!subComponent.checked)}
							className="font-medium leading-[1.2]"
						>
							{subComponent.label}
						</Typography>
					</div>
				);

			case "link":
				return (
					<div className="mt-2">
						<Typography
							variant="txt-16"
							color="blu-medio"
							onClick={subComponent.onClick}
							className="hover:text-blu-chiaro transition-colors"
						>
							{subComponent.label}
						</Typography>
					</div>
				);

			case "text":
				return (
					<div className="mt-2">
						<Typography variant="txt-16" color="blu-medio">
							{subComponent.label}
						</Typography>
					</div>
				);

			case "icon":
				return (
					<div className="mt-2 flex items-center gap-2">
						{subComponent.icon}
						<Typography
							variant="txt-16"
							color="blu-medio"
							onClick={subComponent.onClick}
							className="hover:text-blu-chiaro transition-colors"
						>
							{subComponent.label}
						</Typography>
					</div>
				);

			default:
				return null;
		}
	};

	// Determine input type for password toggle
	const inputType = type === "password" && showPassword ? "text" : type;

	return (
		<div className={cn(
			"flex flex-col gap-4 items-start justify-start",
			fullWidth && "w-full",
			className
		)}>
			{/* Label */}
			{label && (
				<Typography
					variant={sizes.label}
					color={isDisabled ? "grigio" : "blu-scuro"}
					className={cn(
						"font-medium leading-[1.2]",
						labelClassName
					)}
				>
					{label}
					{required && <span className="text-rosso ml-1">*</span>}
				</Typography>
			)}

			{/* Input container */}
			<div className={cn(
				"relative w-full",
				sizes.container
			)}>
				{/* Left icon */}
				{leftIcon && (
					<div className={cn(
						"absolute left-3 top-[20px] -translate-y-1/2 text-blu-scuro/60",
						sizes.icon
					)}>
						{leftIcon}
					</div>
				)}

				{/* Input field */}
				{type === "file" ? (
					<div className={cn(
						// Base classes
						"w-full h-full rounded-[10px] border transition-all duration-200",
						"focus-within:outline-none focus-within:ring-2 focus-within:ring-blu-chiaro/20",
						"flex items-center justify-center cursor-pointer hover:bg-grigio/5",

						// Size and padding
						sizes.input,

						// State-based styling
						stateClasses.border,
						stateClasses.text,
						stateClasses.bg,
						variantClasses,

						// Disabled state
						isDisabled && "cursor-not-allowed opacity-60",

						// Custom classes
						inputClassName
					)}>
						<input
							ref={ref}
							type="file"
							accept={accept}
							multiple={multiple}
							disabled={isDisabled}
							className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
							onChange={handleFileChange}
							{...props}
						/>
						<div className="flex items-center gap-3">
							{leftIcon || (
								<svg
									width="24"
									height="24"
									viewBox="0 0 18 18"
									fill="none"
									className="text-blu-scuro/60"
								>
									<path
										d="M6.32 3.49997L8.88 0.939972L11.44 3.49997M8.88 11.18V1.00997M1 8.99997C1 13.42 4 17 9 17C14 17 17 13.42 17 8.99997"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							)}
							<span className="text-blu-scuro font-medium">
								{placeholder || "Seleziona file..."}
							</span>
						</div>
					</div>
				) : (
					<input
						ref={ref}
						type={inputType}
						placeholder={placeholder}
						disabled={isDisabled}
						className={cn(
							// Base classes
							"w-full h-full rounded-[10px] border transition-all duration-200",
							"focus:outline-none focus:ring-2 focus:ring-blu-chiaro/20",

							// Size and padding
							sizes.input,
							leftIcon && "pl-10",
							rightIcon && "pr-10",

							// State-based styling
							stateClasses.border,
							stateClasses.text,
							stateClasses.bg,
							variantClasses,

							// Disabled state
							isDisabled && "cursor-not-allowed opacity-60",

							// Custom classes
							inputClassName
						)}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						onChange={handleChange}
						{...props}
					/>
				)}

				{/* Right icon (password toggle or custom icon) */}
				{rightIcon && (
					<div className={cn(
						"absolute right-3 top-[20px] -translate-y-1/2 text-blu-scuro/60 cursor-pointer",
						sizes.icon
					)}>
						{rightIcon}
					</div>
				)}

				{/* Password toggle button */}
				{type === "password" && (
					<button
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						className={cn(
							"absolute right-3 top-[21px] -translate-y-1/2 text-blu-scuro/60 hover:text-blu-scuro transition-colors",
							sizes.icon
						)}
					>
						{showPassword ? <IconEyeOpen /> : <IconEyeClosed />}
					</button>
				)}
			</div>

			{/* Helper text */}
			{helperText && !shouldShowError && (
				<Typography
					variant="txt-14"
					color="grigio"
					className="leading-normal"
				>
					{helperText}
				</Typography>
			)}

			{/* Error text */}
			{shouldShowError && errorText && (
				<Typography
					variant="txt-14"
					color="rosso"
					className="leading-normal"
				>
					{errorText}
				</Typography>
			)}

			{/* Success text */}
			{shouldShowSuccess && (
				<Typography
					variant="txt-14"
					color="verde"
					className="leading-normal"
				>
					Operazione completata con successo
				</Typography>
			)}

			{/* Sub-component */}
			{renderSubComponent()}
		</div>
	);
});

Input.displayName = "Input";

// Convenience components for specific input types
export const TextInput: React.FC<Omit<InputProps, "type"> & { type?: never }> = (props) => (
	<Input type="text" {...props} />
);

export const EmailInput: React.FC<Omit<InputProps, "type"> & { type?: never }> = (props) => (
	<Input type="email" {...props} />
);

export const PasswordInput: React.FC<Omit<InputProps, "type"> & { type?: never }> = (props) => (
	<Input type="password" {...props} />
);

export const NumberInput: React.FC<Omit<InputProps, "type"> & { type?: never }> = (props) => (
	<Input type="number" {...props} />
);

export const TelInput: React.FC<Omit<InputProps, "type"> & { type?: never }> = (props) => (
	<Input type="tel" {...props} />
);

export const SearchInput: React.FC<Omit<InputProps, "type"> & { type?: never }> = (props) => (
	<Input type="search" {...props} />
);

export const CheckboxInput: React.FC<{
	label: string;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
	className?: string;
}> = ({ label, checked, onChange, className }) => (
	<div className={cn("flex gap-2 items-center", className)}>
		<div className="relative shrink-0 size-4">
			<input
				type="checkbox"
				checked={checked}
				onChange={(e) => onChange?.(e.target.checked)}
				className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
			/>
			<div className={cn(
				"w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center",
				checked
					? "bg-blu-medio border-blu-medio"
					: "bg-bianco border-blu-medio hover:border-blu-chiaro"
			)}>
				{checked && (
					<svg
						className="w-2.5 h-2.5 text-bianco"
						fill="currentColor"
						viewBox="0 0 20 20"
					>
						<path
							fillRule="evenodd"
							d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
							clipRule="evenodd"
						/>
					</svg>
				)}
			</div>
		</div>
		<Typography
			variant="txt-16"
			color="blu-scuro"
			onClick={() => onChange?.(!checked)}
			className="font-medium leading-[1.2]"
		>
			{label}
		</Typography>
	</div>
);

export const RadioInput: React.FC<{
	label: string;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
	name: string;
	value: string;
	className?: string;
}> = ({ label, checked, onChange, name, value, className }) => (
	<div className={cn("flex gap-2 items-center", className)}>
		<div className="relative shrink-0 size-4">
			<input
				type="radio"
				name={name}
				value={value}
				checked={checked}
				onChange={(e) => onChange?.(e.target.checked)}
				className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
			/>
			<div className={cn(
				"w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center",
				checked
					? "bg-blu-medio border-blu-medio"
					: "bg-bianco border-blu-medio hover:border-blu-chiaro"
			)}>
				{checked && (
					<div className="w-2 h-2 bg-bianco rounded-full"></div>
				)}
			</div>
		</div>
		<Typography
			variant="txt-16"
			color="blu-medio"
			onClick={() => onChange?.(true)}
			className="font-medium leading-[1.2]"
		>
			{label}
		</Typography>
	</div>
);

export const FileInput: React.FC<{
	label?: string;
	placeholder?: string;
	accept?: string;
	multiple?: boolean;
	onFileSelect?: (files: FileList | null) => void;
	helperText?: string;
	errorText?: string;
	state?: InputState;
	showError?: boolean;
	showSuccess?: boolean;
	disabled?: boolean;
	required?: boolean;
	fullWidth?: boolean;
	className?: string;
	leftIcon?: React.ReactNode;
}> = ({
	label,
	placeholder,
	accept,
	multiple,
	onFileSelect,
	helperText,
	errorText,
	state,
	showError,
	showSuccess,
	disabled,
	required,
	fullWidth,
	className,
	leftIcon
}) => (
		<Input
			type="file"
			label={label}
			placeholder={placeholder}
			accept={accept}
			multiple={multiple}
			onFileSelect={onFileSelect}
			helperText={helperText}
			errorText={errorText}
			state={state}
			showError={showError}
			showSuccess={showSuccess}
			disabled={disabled}
			required={required}
			fullWidth={fullWidth}
			className={className}
			leftIcon={leftIcon}
		/>
	);
